import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { environmentSlice, socialSlice, governanceSlice, generalSlice, sustainabilityStorySlice } from "@/slices";
import { useEnvironmentState } from "./wizard/useEnvironmentState";
import { useSocialState } from "./wizard/useSocialState";
import { useGovernanceState } from "./wizard/useGovernanceState";
import { useScope3State } from "./wizard/useScope3State";
import { useSustainabilityStoryState } from "./wizard/useSustainabilityStoryState";
import type { ApplicabilityChecks, DocumentMetadata, GeneralInfo, PolicyTopicData, TransitionMeasure, UploadResult, WizardContextType } from "./wizard/types";
import { useWizardCompletion } from "./wizard/useWizardCompletion";
import { useWizardAutoSave } from "./wizard/useWizardAutoSave";
import {
  deleteAssessmentDocument,
  getAssessmentDocumentUrl,
  uploadAssessmentDocument,
  uploadAssessmentLogo,
} from "@/service/assessmentDocuments";
import { useAuth } from "@/hooks/use-auth";
import {
  getSectionsByTab,
  getSectionById,
  getNextSection,
  getPreviousSection,
  isDeprecatedScope3Section,
  WIZARD_SECTIONS,
  type WizardSection
} from "@shared/section-definitions";

export { POLICY_TOPICS } from "./wizard/constants";
export * from "./wizard/types";

// Topic id -> tab lookup, derived from WIZARD_SECTIONS (the single source of truth for
// topic/tab ownership) — used to route updateTransitionMeasuresForTopic to the correct
// tab's setter. Built once at module scope since WIZARD_SECTIONS is static.
const TOPIC_TAB_LOOKUP: Record<string, WizardSection["tab"]> = Object.fromEntries(
  WIZARD_SECTIONS.map(section => [section.id, section.tab])
);

// ==================== API FUNCTIONS ====================

async function fetchGeneralInformation(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/general-information`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch general information");
  return response.json();
}

async function fetchEnvironment(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/environment`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch environment data");
  return response.json();
}

async function fetchSocial(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/social`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch social data");
  return response.json();
}

async function fetchGovernance(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/governance`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch governance data");
  return response.json();
}

async function fetchSustainabilityStory(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/sustainability-story`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch sustainability story data");
  return response.json();
}

async function fetchCompletion(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/completion`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch completion data");
  return response.json();
}

async function fetchEmployeeData(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/employee-data`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch employee data");
  return response.json();
}

// ==================== CONTEXT ====================

const WizardContext = createContext<WizardContextType | undefined>(undefined);

// ==================== PROVIDER ====================

interface WizardProviderProps {
  children: ReactNode;
  activeTab?: string;
  assessmentId: string; // Assessment ID is now required
  onTabChange?: (tab: string) => void; // Callback for tab changes
  onSectionChange?: (sectionId: string) => void; // Callback for section changes
  initialSectionId?: string; // Initial section from URL
}

export function WizardProvider({ 
  children, 
  activeTab = "general", 
  assessmentId,
  onTabChange,
  onSectionChange,
  initialSectionId
}: WizardProviderProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Section navigation state
  const [currentSectionId, setCurrentSectionId] = useState<string>(() => {
    // Use URL section param if provided and valid
    if (initialSectionId) {
      // Reject deprecated Scope 3 sections
      if (isDeprecatedScope3Section(initialSectionId)) {
        console.warn(`Initial section ${initialSectionId} is deprecated. Defaulting to first section of ${activeTab} tab.`);
        const sections = getSectionsByTab(activeTab);
        return sections[0]?.id || "general-info";
      }
      
      const section = getSectionById(initialSectionId);
      if (section && section.tab === activeTab) {
        return initialSectionId;
      }
    }
    // Otherwise, initialize to first section of active tab
    const sections = getSectionsByTab(activeTab);
    return sections[0]?.id || "general-info";
  });

  // Hydration state tracking - prevents false dirty detection during hydration
  const [hydrationState, setHydrationState] = useState({
    general: { isHydrating: false, isHydrated: false },
    environment: { isHydrating: false, isHydrated: false },
    social: { isHydrating: false, isHydrated: false },
    governance: { isHydrating: false, isHydrated: false },
    "sustainability-story": { isHydrating: false, isHydrated: false },
  });

  // General Information state
  const [generalInfo, setGeneralInfo] = useState<GeneralInfo>({
    companyName: "",
    companyLogoUrl: null,
    reportingCurrency: "USD",
    annualRevenueRange: "",
    totalEmployees: "",
    maleEmployees: "",
    femaleEmployees: "",
    homeworkingPercent: "",
    numberOfSites: "1",
    siteAddresses: [],
    ownershipStructure: "",
    legalForm: "",
    legalFormSubtype: "",
    naceSector: "",
    netTurnover: "",
    balanceSheetTotal: "",
    sustainabilityCertifications: [],
    certificationDocuments: [],
    financialStatementsDocuments: [],
    employeeListDocuments: [],
  });

  // Sustainability & Transition state
  const [descriptionOfActivities, setDescriptionOfActivities] = useState("");
  const [hasSustainabilityPolicies, setHasSustainabilityPolicies] = useState<"yes" | "no" | null>(null);
  const [sustainabilityPolicyDescription, setSustainabilityPolicyDescription] = useState("");
  const [sustainabilityPolicyDocuments, setSustainabilityPolicyDocuments] = useState<DocumentMetadata[]>([]);
  const [policyTopics, setPolicyTopics] = useState<PolicyTopicData[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Applicability checks state
  const [applicabilityChecks, setApplicabilityChecks] = useState<ApplicabilityChecks>({
    usesStationaryFuel: false,
    usesVehicleFuel: false,
    hasRefrigeration: false,
  });

  // ==================== QUERIES ====================

  // Base assessment query - fetch by ID
  const assessmentQuery = useQuery({
    queryKey: ["assessment", assessmentId],
    queryFn: async () => {
      const response = await fetch(`/api/assessments/${assessmentId}`, {
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const error = await response.json().catch(() => ({ error: "Failed to fetch assessment" }));
        throw new Error(error.error || "Failed to fetch assessment");
      }
      return response.json();
    },
    gcTime: 5 * 60 * 1000,
  });

  const isLocked = assessmentQuery.data?.locked || false;

  // Determine if wizard should be read-only (admin viewing another company's assessment)
  const { data: membershipData } = useQuery({
    queryKey: ["workspace-membership"],
    queryFn: async () => {
      const response = await fetch("/api/workspaces/me", { credentials: "include" });
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!user,
  });

  const { state: environmentState, actions: environmentActions } = useEnvironmentState({ country: membershipData?.workspace?.country ?? undefined });
  const { state: socialState, actions: socialActions } = useSocialState();
  const { state: governanceState, actions: governanceActions } = useGovernanceState();
  const { state: sustainabilityStoryState, actions: sustainabilityStoryActions } = useSustainabilityStoryState();
  const { state: scope3State, actions: scope3Actions } = useScope3State();

  // Transition measures: merged across the three tabs that own a slice of transitionMeasuresByTopic,
  // with a single updater that routes to whichever tab's reducer owns a given topicId.
  const transitionMeasuresByTopic = useMemo(
    () => ({
      ...environmentState.transitionMeasuresByTopic,
      ...socialState.transitionMeasuresByTopic,
      ...governanceState.transitionMeasuresByTopic,
    }),
    [environmentState.transitionMeasuresByTopic, socialState.transitionMeasuresByTopic, governanceState.transitionMeasuresByTopic]
  );

  const updateTransitionMeasuresForTopic = useCallback(
    (topicId: string, measures: TransitionMeasure[]) => {
      const tab = TOPIC_TAB_LOOKUP[topicId];
      if (tab === "environment") {
        environmentActions.setTransitionMeasuresForTopic(topicId, measures);
      } else if (tab === "social") {
        socialActions.setTransitionMeasuresForTopic(topicId, measures);
      } else if (tab === "governance") {
        governanceActions.setTransitionMeasuresForTopic(topicId, measures);
      } else {
        // Unknown topicId (typo, or a topic with no TransitionMeasuresBlock, e.g.
        // community-contribution/accountability) — fail loudly instead of silently
        // misrouting to governance.
        console.error(`updateTransitionMeasuresForTopic: unrecognized topicId "${topicId}" — no tab owns this topic's transition measures.`);
      }
    },
    [environmentActions, socialActions, governanceActions]
  );
  const isReadOnly = useMemo(() => {
    if (!assessmentQuery.data || !user) return false;
    // Admin viewing a different company's assessment
    if (user.role === "admin" && membershipData?.workspace?.id !== assessmentQuery.data.workspaceId) {
      return true;
    }
    return false;
  }, [user, assessmentQuery.data, membershipData]);

  // Completion query - always enabled when we have an assessment
  const completionQuery = useQuery({
    queryKey: ["completion", assessmentId],
    queryFn: () => fetchCompletion(assessmentId!),
    enabled: !!assessmentId,
  });

  // General information query - load only when general tab is active
  // Now returns sustainability data merged in the same response
  const generalQuery = useQuery({
    queryKey: ["general-information", assessmentId],
    queryFn: () => fetchGeneralInformation(assessmentId!),
    enabled: !!assessmentId && activeTab === "general",
  });

  // Environment query - load only when environment tab is active
  const environmentQuery = useQuery({
    queryKey: ["environment", assessmentId],
    queryFn: () => fetchEnvironment(assessmentId!),
    enabled: !!assessmentId && activeTab === "environment",
  });

  // Employee data query - load when environment tab is active (for Scope 3 calculations)
  const employeeDataQuery = useQuery({
    queryKey: ["employee-data", assessmentId],
    queryFn: () => fetchEmployeeData(assessmentId!),
    enabled: !!assessmentId && activeTab === "environment",
  });

  // Social query - load only when social tab is active
  const socialQuery = useQuery({
    queryKey: ["social", assessmentId],
    queryFn: () => fetchSocial(assessmentId!),
    enabled: !!assessmentId && activeTab === "social",
  });

  // Governance query - load only when governance tab is active
  const governanceQuery = useQuery({
    queryKey: ["governance", assessmentId],
    queryFn: () => fetchGovernance(assessmentId!),
    enabled: !!assessmentId && activeTab === "governance",
  });

  // Sustainability Story query - load only when sustainability-story tab is active
  const sustainabilityStoryQuery = useQuery({
    queryKey: ["sustainability-story", assessmentId],
    queryFn: () => fetchSustainabilityStory(assessmentId!),
    enabled: !!assessmentId && activeTab === "sustainability-story",
  });

  // Prefetch next tab when tab changes
  useEffect(() => {
    if (!assessmentId || !activeTab) return;

    const tabOrder = ["general", "environment", "social", "governance", "sustainability-story"];
    const currentIndex = tabOrder.indexOf(activeTab);
    const nextTab = tabOrder[currentIndex + 1];

    if (nextTab) {
      if (nextTab === "general") {
        queryClient.prefetchQuery({
          queryKey: generalSlice.queryKey(assessmentId),
          queryFn: () => generalSlice.fetch(assessmentId),
        });
      } else if (nextTab === "environment") {
        queryClient.prefetchQuery({
          queryKey: environmentSlice.queryKey(assessmentId),
          queryFn: () => environmentSlice.fetch(assessmentId),
        });
      } else if (nextTab === "social") {
        queryClient.prefetchQuery({
          queryKey: socialSlice.queryKey(assessmentId),
          queryFn: () => socialSlice.fetch(assessmentId),
        });
      } else if (nextTab === "governance") {
        queryClient.prefetchQuery({
          queryKey: governanceSlice.queryKey(assessmentId),
          queryFn: () => governanceSlice.fetch(assessmentId),
        });
      } else if (nextTab === "sustainability-story") {
        queryClient.prefetchQuery({
          queryKey: sustainabilityStorySlice.queryKey(assessmentId),
          queryFn: () => sustainabilityStorySlice.fetch(assessmentId),
        });
      }
    }
  }, [activeTab, assessmentId, queryClient]);

  // ==================== HYDRATION ====================

  // General slice hydration — sustainability data now comes from same response
  useEffect(() => {
    const data = generalQuery.data;
    // Use === undefined to distinguish "not loaded yet" from "loaded but null/empty"
    if (data === undefined || hydrationState.general.isHydrated) return;

    setHydrationState(prev => ({
      ...prev,
      general: { isHydrating: true, isHydrated: false }
    }));

    // Data from single endpoint already includes policiesByTopic
    const apiData = {
      generalInfo: data ?? {},
    };

    generalSlice.hydrate(apiData, {
      setGeneralInfo: setGeneralInfo,
      setDescriptionOfActivities: setDescriptionOfActivities,
      setHasSustainabilityPolicies: setHasSustainabilityPolicies,
      setSustainabilityPolicyDescription: setSustainabilityPolicyDescription,
      setSustainabilityPolicyDocuments: setSustainabilityPolicyDocuments,
      setPolicyTopics: setPolicyTopics,
      setSuggestionsDismissed: (dismissed: boolean) => setShowSuggestions(!dismissed),
    });

    setHydrationState(prev => ({
      ...prev,
      general: { isHydrating: false, isHydrated: true }
    }));
  }, [generalQuery.data]);

  // Environment slice hydration
  useEffect(() => {
    const data = environmentQuery.data;
    // Use === undefined to distinguish "not loaded yet" from "loaded but null/empty"
    // API returns null for new assessments with no saved environment data
    if (data === undefined || hydrationState.environment.isHydrated) return;

    setHydrationState(prev => ({
      ...prev,
      environment: { isHydrating: true, isHydrated: false }
    }));

    environmentSlice.hydrate(data ?? {}, {
      setFuelEntries: environmentActions.setFuelEntries,
      setElectricityEntries: environmentActions.setElectricityEntries,
      setRefrigerantEntries: environmentActions.setRefrigerantEntries,
      setCarbonSupportingDocuments: environmentActions.setCarbonSupportingDocuments,
      setPollutionData: environmentActions.setPollutionData,
      setBiodiversityData: environmentActions.setBiodiversityData,
      setBiodiversitySupportingDocuments: environmentActions.setBiodiversitySupportingDocuments,
      setWaterData: environmentActions.setWaterData,
      setWaterSupportingDocuments: environmentActions.setWaterSupportingDocuments,
      setWasteData: environmentActions.setWasteData,
      setWasteSupportingDocuments: environmentActions.setWasteSupportingDocuments,
      setScope3DataFromApi: (scope3Data) => scope3Actions.setScope3Data(scope3Data),
      setRiskManagementData: environmentActions.setRiskManagementData,
      setTransitionMeasuresForTopic: environmentActions.setTransitionMeasuresForTopic,
    });

    setHydrationState(prev => ({
      ...prev,
      environment: { isHydrating: false, isHydrated: true }
    }));
  }, [environmentQuery.data, environmentActions, scope3Actions]);

  // Social slice hydration
  useEffect(() => {
    const data = socialQuery.data;
    // Use === undefined to distinguish "not loaded yet" from "loaded but null/empty"
    if (data === undefined || hydrationState.social.isHydrated) return;

    setHydrationState(prev => ({
      ...prev,
      social: { isHydrating: true, isHydrated: false }
    }));

    socialSlice.hydrate(data ?? {}, {
      setEmployeeSupportData: socialActions.setEmployeeSupportData,
      setHumanRightsData: socialActions.setHumanRightsData,
      setDiversityInclusionData: socialActions.setDiversityInclusionData,
      setCommunityContributionData: socialActions.setCommunityContributionData,
      setSocialData: socialActions.setSocialData,
      setTransitionMeasuresForTopic: socialActions.setTransitionMeasuresForTopic,
    });

    setHydrationState(prev => ({
      ...prev,
      social: { isHydrating: false, isHydrated: true }
    }));
  }, [socialQuery.data, socialActions]);

  // Governance slice hydration
  useEffect(() => {
    const data = governanceQuery.data;
    // Use === undefined to distinguish "not loaded yet" from "loaded but null/empty"
    if (data === undefined || hydrationState.governance.isHydrated) return;

    setHydrationState(prev => ({
      ...prev,
      governance: { isHydrating: true, isHydrated: false }
    }));

    governanceSlice.hydrate(data ?? {}, {
      setEthicsData: governanceActions.setEthicsData,
      setTransparencyData: governanceActions.setTransparencyData,
      setLeadershipData: governanceActions.setLeadershipData,
      setAccountabilityData: governanceActions.setAccountabilityData,
      setTransitionMeasuresForTopic: governanceActions.setTransitionMeasuresForTopic,
    });

    setHydrationState(prev => ({
      ...prev,
      governance: { isHydrating: false, isHydrated: true }
    }));
  }, [governanceQuery.data, governanceActions]);

  // Sustainability Story slice hydration
  useEffect(() => {
    const data = sustainabilityStoryQuery.data;
    if (data === undefined || hydrationState["sustainability-story"].isHydrated) return;

    setHydrationState(prev => ({
      ...prev,
      "sustainability-story": { isHydrating: true, isHydrated: false }
    }));

    sustainabilityStorySlice.hydrate(data ?? {}, {
      setStoryGeneralData: sustainabilityStoryActions.setStoryGeneralData,
      setStoryEnvironmentData: sustainabilityStoryActions.setStoryEnvironmentData,
      setStorySocialData: sustainabilityStoryActions.setStorySocialData,
      setStoryGovernanceData: sustainabilityStoryActions.setStoryGovernanceData,
    });

    setHydrationState(prev => ({
      ...prev,
      "sustainability-story": { isHydrating: false, isHydrated: true }
    }));
  }, [sustainabilityStoryQuery.data, sustainabilityStoryActions]);

  // Sync generalInfo workforce fields → employeeSupportData (auto-fill empty fields only)
  useEffect(() => {
    if (!hydrationState.general.isHydrated || !hydrationState.social.isHydrated) return;

    const updates: Partial<typeof socialState.employeeSupportData> = {};
    if (!socialState.employeeSupportData.permanentEmployees && generalInfo.totalEmployees) {
      updates.permanentEmployees = generalInfo.totalEmployees;
    }
    if (!socialState.employeeSupportData.maleEmployees && generalInfo.maleEmployees) {
      updates.maleEmployees = generalInfo.maleEmployees;
    }
    if (!socialState.employeeSupportData.femaleEmployees && generalInfo.femaleEmployees) {
      updates.femaleEmployees = generalInfo.femaleEmployees;
    }
    if (Object.keys(updates).length > 0) {
      socialActions.updateEmployeeSupportData(updates);
    }
  }, [
    hydrationState.general.isHydrated,
    hydrationState.social.isHydrated,
    generalInfo.totalEmployees,
    generalInfo.maleEmployees,
    generalInfo.femaleEmployees,
    socialState.employeeSupportData.permanentEmployees,
    socialState.employeeSupportData.maleEmployees,
    socialState.employeeSupportData.femaleEmployees,
    socialActions,
  ]);

  // ==================== HELPERS ====================

  const updateGeneralInfo = useCallback((updates: Partial<GeneralInfo>) => {
    setGeneralInfo(prev => ({ ...prev, ...updates }));
  }, []);

  const updateApplicabilityChecks = useCallback((updates: Partial<ApplicabilityChecks>) => {
    setApplicabilityChecks(prev => ({ ...prev, ...updates }));
  }, []);

  // Document upload/delete methods
  const uploadFile = useCallback(
    async (file: File, type: "logo"): Promise<UploadResult<string>> => {
      if (!assessmentId) return { ok: false, error: "No assessment selected" };

      try {
        if (type === "logo") {
          const response = await uploadAssessmentLogo(assessmentId, file, type);
          if (response.ok) {
            const result = await response.json();
            return { ok: true, data: result.fileUrl as string };
          }
          const body = await response.json().catch(() => ({}));
          return { ok: false, error: body.error ?? body.message ?? "Upload failed — please try again" };
        }
        return { ok: false, error: "Upload failed — please try again" };
      } catch (error) {
        console.error("Logo upload error:", error);
        return { ok: false, error: "Upload failed — please try again" };
      }
    },
    [assessmentId]
  );

  const uploadDocument = useCallback(
    async (file: File, fieldIdentifier: string, currentTab?: "general" | "environment" | "social" | "governance"): Promise<UploadResult<DocumentMetadata>> => {
      if (!assessmentId) return { ok: false, error: "No assessment selected" };

      try {
        const response = await uploadAssessmentDocument(assessmentId, file, fieldIdentifier);
        if (response.ok) {
          const result = (await response.json()) as DocumentMetadata;
          return { ok: true, data: result };
        }
        const body = await response.json().catch(() => ({}));
        return { ok: false, error: body.error ?? body.message ?? "Upload failed — please try again" };
      } catch (error) {
        console.error("Document upload error:", error);
        return { ok: false, error: "Upload failed — please try again" };
      }
    },
    [assessmentId]
  );

  const deleteDocument = useCallback(
    async (filename: string) => {
      if (!assessmentId) return false;

      try {
        const response = await deleteAssessmentDocument(assessmentId, filename);
        return response.ok;
      } catch (error) {
        console.error("Document delete error:", error);
        return false;
      }
    },
    [assessmentId]
  );

  const getDocumentDownloadUrl = useCallback(
    (filename: string) => {
      if (!assessmentId) return "";
      return getAssessmentDocumentUrl(assessmentId, filename);
    },
    [assessmentId]
  );

  // ==================== COMPLETION & AUTO-SAVE ====================

  const {
    overallCompletion,
    generalOverallCompletion,
    generalInfoCardCompletion,
    sustainabilityCardCompletion,
    environmentOverallCompletion,
    carbonCompletion,
    businessTravelCompletion,
    employeeCommutingCompletion,
    accommodationCompletion,
    companyExpensesCompletion,
    freightCompletion,
    pollutionCompletion,
    biodiversityCompletion,
    waterCompletion,
    wasteCompletion,
    socialOverallCompletion,
    workforceCompletion,
    diversityInclusionCompletion,
    humanRightsCompletion,
    communityContributionCompletion,
    governanceOverallCompletion,
    ethicsCompletion,
    transparencyCompletion,
    leadershipCompletion,
    riskManagementCompletion,
    accountabilityCompletion,
    storyOverallCompletion,
    storyGeneralCompletion,
    storyEnvironmentCompletion,
    storySocialCompletion,
    storyGovernanceCompletion,
  } = useWizardCompletion({
    completionData: completionQuery.data,
    scope3Data: scope3State.scope3Data,
  });

  // Auto-save functionality
  const { saveStatus, lastSaved, forceSave } = useWizardAutoSave({
    assessmentId,
    activeTab: activeTab as "general" | "environment" | "social" | "governance" | "sustainability-story",
    isLocked: isLocked || isReadOnly,
    hydrationState,
    generalInfo,
    descriptionOfActivities,
    hasSustainabilityPolicies,
    sustainabilityPolicyDescription,
    sustainabilityPolicyDocuments,
    policyTopics,
    showSuggestions,
    applicabilityChecks,
    ...environmentState,
    scope3Data: scope3State.scope3Data,
    environmentTransitionMeasuresByTopic: environmentState.transitionMeasuresByTopic,
    ...socialState,
    socialTransitionMeasuresByTopic: socialState.transitionMeasuresByTopic,
    ...governanceState,
    governanceTransitionMeasuresByTopic: governanceState.transitionMeasuresByTopic,
    ...sustainabilityStoryState,
    onSaveSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["completion", assessmentId] });
    },
  });

  // ==================== LOADING STATES ====================

  const dataLoaded = assessmentQuery.isSuccess;
  const loadingError = assessmentQuery.error?.message || null;

  // Tab-specific loading states
  const isLoadingGeneral = generalQuery.isPending;
  const isLoadingEnvironment = environmentQuery.isPending || employeeDataQuery.isPending;
  const isLoadingSocial = socialQuery.isPending;
  const isLoadingGovernance = governanceQuery.isPending;

  // ==================== SECTION NAVIGATION ====================

  // Navigation methods
  const goToSection = useCallback((sectionId: string) => {
    // Redirect deprecated Scope 3 sections to Environment overview
    if (isDeprecatedScope3Section(sectionId)) {
      console.warn(`Section ${sectionId} is deprecated and now nested under Carbon Emissions. Redirecting to Environment overview.`);
      // Redirect to environment tab overview (no specific section expanded)
      if (activeTab !== "environment" && onTabChange) {
        onTabChange("environment");
      }
      // Clear section to show grid view
      setCurrentSectionId("");
      if (onSectionChange) {
        onSectionChange("");
      }
      return;
    }

    const section = getSectionById(sectionId);
    if (!section) return;

    setCurrentSectionId(sectionId);
    
    // If section is in a different tab, trigger tab change
    if (section.tab !== activeTab && onTabChange) {
      onTabChange(section.tab);
    }

    // Notify about section change
    if (onSectionChange) {
      onSectionChange(sectionId);
    }
  }, [activeTab, onTabChange, onSectionChange]);

  const goToNextSection = useCallback(() => {
    const { section: nextSection, nextTab } = getNextSection(activeTab, currentSectionId);
    
    if (nextSection) {
      setCurrentSectionId(nextSection.id);
      
      // If moving to a new tab, trigger tab change
      if (nextTab && onTabChange) {
        onTabChange(nextTab);
      }

      // Notify about section change
      if (onSectionChange) {
        onSectionChange(nextSection.id);
      }
    }
    
    return { nextSection, nextTab };
  }, [activeTab, currentSectionId, onTabChange, onSectionChange]);

  const goToPreviousSection = useCallback(() => {
    const { section: prevSection, previousTab } = getPreviousSection(activeTab, currentSectionId);
    
    if (prevSection) {
      setCurrentSectionId(prevSection.id);
      
      // If moving to a previous tab, trigger tab change
      if (previousTab && onTabChange) {
        onTabChange(previousTab);
      }

      // Notify about section change
      if (onSectionChange) {
        onSectionChange(prevSection.id);
      }
    }
    
    return { prevSection, previousTab };
  }, [activeTab, currentSectionId, onTabChange, onSectionChange]);

  // Sync section when tab changes externally
  useEffect(() => {
    const currentSection = getSectionById(currentSectionId);
    if (currentSection && currentSection.tab !== activeTab) {
      // Tab changed, reset to first section of new tab
      const sections = getSectionsByTab(activeTab);
      if (sections[0]) {
        setCurrentSectionId(sections[0].id);
      }
    }
  }, [activeTab, currentSectionId]);

  // ==================== CONTEXT VALUE ====================

  const value = useMemo<WizardContextType>(
    () => ({
      assessmentId,
      dataLoaded,
      loadingError,
      assessment: assessmentQuery.data || null,
      isLocked,
      isReadOnly,

      // Hydration state
      hydrationState,

      // Loading states per tab
      isLoadingGeneral,
      isLoadingEnvironment,
      isLoadingSocial,
      isLoadingGovernance,
      isLoadingSustainabilityStory: sustainabilityStoryQuery.isPending,

      // Section navigation
      currentSectionId,
      currentSection: getSectionById(currentSectionId),
      goToSection,
      goToNextSection,
      goToPreviousSection,

      // General info
      generalInfo,
      updateGeneralInfo,

      // Sustainability
      descriptionOfActivities,
      setDescriptionOfActivities,
      hasSustainabilityPolicies,
      setHasSustainabilityPolicies,
      sustainabilityPolicyDescription,
      setSustainabilityPolicyDescription,
      sustainabilityPolicyDocuments,
      setSustainabilityPolicyDocuments,
      policyTopics,
      setPolicyTopics,
      showSuggestions,
      setShowSuggestions,

      // Applicability checks
      applicabilityChecks,
      updateApplicabilityChecks,

      // Environment state and actions
      ...environmentState,
      ...environmentActions,

      // Scope 3 state and actions
      ...scope3State,
      scope3CoverageData: scope3State.scope3Data.coverageData,
      ...scope3Actions,

      // Social state and actions
      ...socialState,
      ...socialActions,

      // Governance state and actions
      ...governanceState,
      ...governanceActions,

      // Transition measures (merged across environment/social/governance, routed by topicId)
      transitionMeasuresByTopic,
      updateTransitionMeasuresForTopic,

      // Sustainability Story state and actions
      ...sustainabilityStoryState,
      updateStoryGeneralData: sustainabilityStoryActions.updateStoryGeneralData,
      updateStoryEnvironmentData: sustainabilityStoryActions.updateStoryEnvironmentData,
      updateStorySocialData: sustainabilityStoryActions.updateStorySocialData,
      updateStoryGovernanceData: sustainabilityStoryActions.updateStoryGovernanceData,

      // Auto-save
      saveStatus,
      lastSaved,
      forceSave,

      // Completion
      overallCompletion,
      generalOverallCompletion,
      generalInfoCardCompletion,
      sustainabilityCardCompletion,
      environmentOverallCompletion,
      carbonCompletion,
      businessTravelCompletion,
      employeeCommutingCompletion,
      accommodationCompletion,
      companyExpensesCompletion,
      freightCompletion,
      pollutionCompletion,
      biodiversityCompletion,
      waterCompletion,
      wasteCompletion,
      socialOverallCompletion,
      workforceCompletion,
      diversityInclusionCompletion,
      humanRightsCompletion,
      communityContributionCompletion,
      governanceOverallCompletion,
      ethicsCompletion,
      transparencyCompletion,
      leadershipCompletion,
      riskManagementCompletion,
      accountabilityCompletion,
      storyOverallCompletion,
      storyGeneralCompletion,
      storyEnvironmentCompletion,
      storySocialCompletion,
      storyGovernanceCompletion,

      // Document management
      uploadFile,
      uploadDocument,
      deleteDocument,
      getDocumentDownloadUrl,
    }),
    [
      assessmentId,
      dataLoaded,
      loadingError,
      assessmentQuery.data,
      isLocked,
      isReadOnly,
      hydrationState,
      isLoadingGeneral,
      isLoadingEnvironment,
      isLoadingSocial,
      isLoadingGovernance,
      generalInfo,
      updateGeneralInfo,
      descriptionOfActivities,
      hasSustainabilityPolicies,
      sustainabilityPolicyDescription,
      sustainabilityPolicyDocuments,
      policyTopics,
      showSuggestions,
      applicabilityChecks,
      updateApplicabilityChecks,
      environmentState,
      environmentActions,
      scope3State,
      scope3Actions,
      socialState,
      socialActions,
      governanceState,
      governanceActions,
      transitionMeasuresByTopic,
      updateTransitionMeasuresForTopic,
      sustainabilityStoryState,
      sustainabilityStoryActions,
      saveStatus,
      lastSaved,
      forceSave,
      overallCompletion,
      generalOverallCompletion,
      generalInfoCardCompletion,
      sustainabilityCardCompletion,
      environmentOverallCompletion,
      carbonCompletion,
      businessTravelCompletion,
      employeeCommutingCompletion,
      accommodationCompletion,
      companyExpensesCompletion,
      freightCompletion,
      pollutionCompletion,
      biodiversityCompletion,
      waterCompletion,
      wasteCompletion,
      socialOverallCompletion,
      workforceCompletion,
      diversityInclusionCompletion,
      humanRightsCompletion,
      communityContributionCompletion,
      governanceOverallCompletion,
      ethicsCompletion,
      transparencyCompletion,
      leadershipCompletion,
      riskManagementCompletion,
      accountabilityCompletion,
      storyOverallCompletion,
      storyGeneralCompletion,
      storyEnvironmentCompletion,
      storySocialCompletion,
      storyGovernanceCompletion,
      uploadFile,
      uploadDocument,
      deleteDocument,
      getDocumentDownloadUrl,
    ]
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

// ==================== HOOK ====================

export function useWizard() {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error("useWizard must be used within a WizardProvider");
  }
  return context;
}
